import { readSession } from '@/helpers/storage'
import type { ApiResponse, Contact, CrmAction, Note } from '@/models/api'
import { api, readData } from '@/services/apiClient'

export interface CustomerDetail {
  id: string
  name: string
  accountNumber: string | null
  address1: string | null
  address2: string | null
  address3: string | null
  city: string | null
  county: string | null
  postcode: string | null
  callDay: string | null
  deliveryDay: string | null
  contactNumber: string | null
  emailAddress: string | null
  notes: string | null
  url: string | null
  preferredContactMethod: string | null
  onStop: boolean
  accountOwnerId: string
  accountOwnerName: string | null
  customerTypeId: number
  customerType: string | null
  statusId: number
  gridRefId: number
  gridRef: string | null
  defaultDiscountId: number
  discount: string | null
  categoryId: number | null
  category: string | null
  status: string | null
  imageUrl: string | null
  deleted: boolean
  contacts: Contact[]
  customerNotes: Note[]
  actions: CrmAction[]
}

export interface CustomerOrder {
  id: number
  customerId?: string
  customerName?: string | null
  formId?: number | null
  formName: string | null
  status: string
  dateModified?: string
  placedAt: string
  placedByName: string
}

export interface CustomerInput {
  onStop?: boolean
  stopReason?: string
  id?: string | null
  name: string
  accountNumber: string | null
  address1: string | null
  address2: string | null
  address3: string | null
  city: string | null
  county: string | null
  postcode: string | null
  callDay: string | null
  deliveryDay: string | null
  contactNumber: string | null
  emailAddress: string | null
  notes: string | null
  url: string | null
  preferredContactMethod: string | null
  accountOwnerId: string
  customerTypeId: number
  statusId: number
  gridRefId: number
  defaultDiscountId: number
  categoryId: number | null
}

export async function uploadCustomerImage(id: string, file: File): Promise<string> {
  const body = new FormData()
  body.append('file', file)
  const { data } = await api.post<ApiResponse<string>>(`/customers/${id}/image`, body)
  return readData(data, '')
}

export async function removeCustomerImage(id: string): Promise<void> {
  const { data } = await api.delete<ApiResponse<null>>(`/customers/${id}/image`)
  readData(data, null)
}

export async function saveCustomer(input: CustomerInput): Promise<string> {
  if (input.id) {
    const { data } = await api.put<ApiResponse<string>>(`/customers/${input.id}`, input)
    return readData(data, input.id)
  }
  const { data } = await api.post<ApiResponse<string>>('/customers', input)
  return readData(data, '')
}

export async function loadCustomer(id: string): Promise<CustomerDetail> {
  const { data } = await api.get<ApiResponse<CustomerDetail>>(`/customers/${id}`)
  const customer = readData(data, null as unknown as CustomerDetail)
  return {
    ...customer,
    contacts: customer.contacts ?? [],
    customerNotes: [...(customer.customerNotes ?? [])].sort((a, b) => new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime()),
    actions: customer.actions ?? [],
  }
}

export interface OrderFormOption {
  id: number
  formName: string
  hasPublishedVersion: boolean
}

export interface PublishedForm {
  currentVersionId: number | null
  id: number
  formName: string
  formJson: string
  publishedFormJson: string | null
  hasPublishedVersion: boolean
}

export async function loadForm(id: number): Promise<PublishedForm> {
  const { data } = await api.get<ApiResponse<PublishedForm>>(`/forms/${id}`)
  return readData(data, { id, currentVersionId: null, formName: '', formJson: '{}', publishedFormJson: null, hasPublishedVersion: false })
}

export async function placeOrder(input: { formId: number; formVersionId: number; customerId: string; orderJson: string; actionId?: string | null }): Promise<number> {
  const session = readSession()
  const storageKey = `crm-order-retry:${session?.userId}:${session?.businessId}`
  const payload = JSON.stringify(input)
  let pending: { payload: string; requestId: string } | null = null
  try { pending = JSON.parse(sessionStorage.getItem(storageKey) || 'null') } catch { /* new request */ }
  if (pending?.payload !== payload) pending = { payload, requestId: crypto.randomUUID() }
  sessionStorage.setItem(storageKey, JSON.stringify(pending))
  const { data } = await api.post<ApiResponse<number>>('/orders', { ...input, requestId: pending!.requestId })
  const id = readData(data, 0)
  sessionStorage.removeItem(storageKey)
  return id
}

export async function loadOrderForms(): Promise<OrderFormOption[]> {
  const { data } = await api.get<ApiResponse<OrderFormOption[]>>('/orders/forms')
  return readData(data, [])
}

async function loadOrders(params: Record<string, string>): Promise<CustomerOrder[]> {
  const rows: CustomerOrder[] = []
  let total = 0
  for (let page = 1; page === 1 || rows.length < total; page++) {
    const { data } = await api.get<ApiResponse<CustomerOrder[]>>('/orders', { params: { ...params, page, pageSize: 200 } })
    const next = readData(data, [])
    total = Number(data.extraData ?? next.length)
    rows.push(...next)
    if (!next.length) break
  }
  return rows
}
export async function loadCustomerOrders(customerId: string): Promise<CustomerOrder[]> {
  return loadOrders({ customerId })
}
export async function loadOpenOrders(): Promise<CustomerOrder[]> {
  const [submitted, processing] = await Promise.all([loadOrders({ status: 'Submitted' }), loadOrders({ status: 'Processing' })])
  return [...submitted, ...processing].sort((a, b) => new Date(a.placedAt).getTime() - new Date(b.placedAt).getTime())
}

export async function markOrderProcessed(id: number, expectedDateModified?: string): Promise<void> {
  const { data } = await api.post<ApiResponse<null>>(`/orders/${id}/status`, { orderId: id, status: 3, expectedDateModified })
  readData(data, null)
}

export async function loadCustomerNotes(customerId: string, page = 1, pageSize = 10): Promise<{ notes: Note[]; total: number }> {
  const { data } = await api.get<ApiResponse<Array<Note & { customerId?: string }>>>(`/notes/customer/${customerId}`, { params: { page, pageSize } })
  const rows = readData(data, [])
  const total = typeof data.extraData === 'number' ? data.extraData : Number(data.extraData ?? rows.length)
  return {
    total: Number.isFinite(total) ? total : rows.length,
    notes: rows.map((row) => ({ ...row, organisationId: row.organisationId || row.customerId || customerId })),
  }
}

export async function saveCustomerNote(customerId: string, noteText: string, id?: string): Promise<string> {
  const { data } = await api.post<ApiResponse<string>>('/notes', { id: id ?? null, customerId, noteText })
  return readData(data, id ?? '')
}

export async function deleteCustomerNote(id: string): Promise<void> {
  const { data } = await api.delete<ApiResponse<null>>(`/notes/${id}`)
  readData(data, null)
}

export async function setCustomerStop(customerId: string, onStop: boolean, reason: string): Promise<void> {
  const { data } = await api.put<ApiResponse<null>>(`/customers/${customerId}/stop`, { customerId, onStop, reason })
  readData(data, null)
}

export interface CustomerResult {
  id: string
  name: string
  accountNumber: string | null
  city: string | null
  postcode: string | null
  customerType: string | null
  category: string | null
  accountOwnerName: string | null
  address1: string | null
  address2: string | null
  address3: string | null
  county: string | null
  discount: string | null
  gridRef: string | null
  contactNumber: string | null
  onStop: boolean
  deleted: boolean
  contacts: Contact[]
}

export interface CustomerSearch {
  search?: string
  name?: string
  postcode?: string
  accountNumber?: string
  includeDeleted?: boolean
  ownerUserId?: string
}

export async function searchCustomerPage(query: CustomerSearch, page = 1, pageSize = 20): Promise<{ rows: CustomerResult[]; total: number }> {
  const { data } = await api.post<ApiResponse<CustomerResult[]>>('/customers/search', {
    page, pageSize,
    searchValue: query.search?.trim() || null,
    name: query.name?.trim() || null,
    postcode: query.postcode?.trim() || null,
    accountNumber: query.accountNumber?.trim() || null,
    includeDeleted: query.includeDeleted ?? false,
    ownerUserId: query.ownerUserId || null,
  })
  const rows = readData(data, []).map((row) => ({ ...row, contacts: row.contacts ?? [] }))
  return { rows, total: Number(data.extraData ?? rows.length) }
}

export async function searchCustomers(query: CustomerSearch): Promise<CustomerResult[]> {
  const first = await searchCustomerPage(query, 1, 200)
  const rows = [...first.rows]
  for (let page = 2; rows.length < first.total; page++) {
    const next = await searchCustomerPage(query, page, 200)
    if (!next.rows.length) break
    rows.push(...next.rows)
  }
  return rows
}

export async function customerDeleteCheck(id: string): Promise<{ openActions: number; canDelete: boolean }> {
  const { data } = await api.get<ApiResponse<{ openActions: number; canDelete: boolean }>>(`/customers/${id}/delete-check`)
  return readData(data, { openActions: 0, canDelete: false })
}
export async function deleteCustomer(id: string): Promise<void> {
  const check = await customerDeleteCheck(id)
  if (!check.canDelete) throw new Error(`Complete or delete this customer's ${check.openActions} open action(s) before deleting the customer.`)
  const { data } = await api.delete<ApiResponse<null>>(`/customers/${id}`)
  readData(data, null)
}
export async function restoreCustomer(id: string): Promise<void> {
  const { data } = await api.post<ApiResponse<null>>(`/customers/${id}/restore`)
  readData(data, null)
}
